import React, { useState, useEffect } from 'react';
import { 
  Upload as UploadIcon,
  CheckCircle, 
  FolderOpen, 
  Tag, 
  Settings,
  AlertCircle,
  X,
  File,
  FileText,
  MoreVertical
} from "lucide-react";
import { Button, message, Dropdown, Menu } from 'antd';
import axios from '../../services/axios';
import { useLocation, useNavigate } from 'react-router-dom';

const AmountInput = ({ label, value, onChange, readOnly = false }) => (
  <div>
    <label className="block text-sm font-semibold text-gray-700 mb-2">
      {label}
    </label>

    <input
      type="text"
      inputMode="decimal"
      value={value ?? ""}
      readOnly={readOnly}
      onChange={(e) => {
        if (readOnly) return;

        const val = e.target.value;
        if (/^[+-]?\d*\.?\d*$/.test(val)) {
          onChange(val);
        }
      }}
      className={`w-full px-4 py-3 border rounded-xl ${
        readOnly
          ? "bg-gray-100 cursor-not-allowed border-gray-200"
          : "border-gray-200 focus:ring-2 focus:ring-blue-500"
      }`}
    />
  </div>
);


const DocumentUploadForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    type: '',
    category: '',
    discipline: '',
    documentReference: '',
    documentDescription: '',
    respondingTo: '',
    projectName: JSON.parse(localStorage.getItem('Project'))
  });
  const [categoryFields, setCategoryFields] = useState({
    ipaReference: '',
    ipcReference: '',
    preliminaries: '',
    workDone: '',
    provisionalSums: '',
    approvedVariations: '',
    materialsOnSite: '',
    claimsOther: '',
    advancesAdjustments: '',
    deductionsPenalty: '',
    total: '',
    lessPrevCertified: '',
    netAmountDue: '',
    vat: '',
    grandTotal: '',
    chequeNo: '',
    paymentReceived: '',
    ipcRef: '',
    remarks: '',
  });
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [isReupload, setIsReupload] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [errors, setErrors] = useState({});
  const [documentTypes, setDocumentTypes] = useState([]);
  const [documentCategories, setDocumentCategories] = useState([]);
  const [originalDocumentCode, setOriginalDocumentCode] = useState('');
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [pendingUploadPayload, setPendingUploadPayload] = useState(null);
  const isUploadResponse = location.state?.from === "upload-response";
  const user = localStorage.getItem('user');
  const role = JSON.parse(localStorage.getItem('role'))[0];
  const disciplines = [
    "Architectural", "BMS", "CCTV", "Coordination", "Civil", "Electrical", 
    "Finishes", "Fire Fighting", "Fire Alarm", "General", "HVAC", "IT", 
    "ID Works", "Mechanical", "Plumbing", "Provisional Sum Items", "Structural", "Telecom", "General"
  ];
  const isIPA = formData.category === "Interim Payment Application (IPA)";
  const isIPC = formData.category === "Interim Payment Certificate (IPC)";
  const isPYR = formData.category === "Payment Receipt (PYR)";

  const handleCategoryFieldChange = (field, value) => {
    setCategoryFields(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const toNumber = (val) => {
    if (val === '' || val === '-' || val === '+') return 0;
    const num = Number(val);
    return isNaN(num) ? 0 : num;
  };

  useEffect(() => {
    if (!isIPA && !isIPC) return;

    const preliminaries       = toNumber(categoryFields.preliminaries);
    const workDone            = toNumber(categoryFields.workDone);
    const provisionalSums     = toNumber(categoryFields.provisionalSums);
    const approvedVariations  = toNumber(categoryFields.approvedVariations);
    const materialsOnSite     = toNumber(categoryFields.materialsOnSite);
    const claimsOther         = toNumber(categoryFields.claimsOther);
    const advancesAdjustments = toNumber(categoryFields.advancesAdjustments);
    const deductionsPenalty  = toNumber(categoryFields.deductionsPenalty);
    const lessPrevCertified  = toNumber(categoryFields.lessPrevCertified);
    const vat                 = toNumber(categoryFields.vat);

    // ✅ EXACT formula you mentioned
    const total =
      preliminaries +
      workDone +
      provisionalSums +
      approvedVariations +
      materialsOnSite +
      claimsOther +
      advancesAdjustments -
      deductionsPenalty;

    const netAmountDue = total - lessPrevCertified;
    const grandTotal   = netAmountDue + vat;

    setCategoryFields(prev => ({
      ...prev,
      total: total.toFixed(2),
      netAmountDue: netAmountDue.toFixed(2),
      grandTotal: grandTotal.toFixed(2),
    }));
  }, [
    categoryFields.preliminaries,
    categoryFields.workDone,
    categoryFields.provisionalSums,
    categoryFields.approvedVariations,
    categoryFields.materialsOnSite,
    categoryFields.claimsOther,
    categoryFields.advancesAdjustments,
    categoryFields.deductionsPenalty,
    categoryFields.lessPrevCertified,
    categoryFields.vat,
    isIPA,
    isIPC
  ]);

  useEffect(() => {
    getUserDetails();

    if (location.state?.prefillData && !isUploadResponse) {
      const prefillData = location.state.prefillData;
      setIsReupload(true);
      setOriginalDocumentCode(prefillData.originalDocumentCode);
      setFormData({
        type: prefillData.type,
        category: prefillData.category,
        discipline: prefillData.discipline,
        documentReference: prefillData.documentReference,
        documentDescription: prefillData.documentDescription,
        projectName: prefillData.projectName,
      });
    }

    if (isUploadResponse && location.state?.prefillData) {
      const prefill = location.state.prefillData;

      setIsReupload(false);
      setOriginalDocumentCode(prefill.originalDocumentCode);

      setFormData(prev => ({
        ...prev,
        type: prefill.type,
        category: prefill.category,
        discipline: prefill.discipline,
        respondingTo: prefill.respondingTo,
        documentReference: prefill.documentReference,
        documentDescription: prefill.documentDescription,
        projectName: prefill.projectName,
      }));
    }
  }, [location.state]);

  const getUserDetails = async () => {
    try {
      const res = await axios.post('get-user-details', { user });
      if (res?.data?.userDetails) {
        if (res?.data?.userDetails.documentType) {
          setDocumentTypes(res.data.userDetails.documentType);
          if (res.data.userDetails.documentType.length === 1) {
            setFormData(prev => ({ ...prev, type: res.data.userDetails.documentType[0] }));
          }
        }
        if (res?.data?.userDetails.documentCategory) {
          setDocumentCategories(res.data.userDetails.documentCategory);
          if (res.data.userDetails.documentCategory.length === 1) {
            setFormData(prev => ({ ...prev, category: res.data.userDetails.documentCategory[0] }));
          }
        }
      }
    } catch (err) {
      if (err.response && err.response.data && err.response.data.msg) {
        setErrors(prev => ({ ...prev, general: err.response.data.msg }));
      } else if (err.response && err.response.data && err.response.data.userName) {
        setErrors(prev => ({ ...prev, general: err.response.data.userName[0] }));
      } else if (err.response && err.response.status === 401) {
        setErrors(prev => ({ ...prev, general: 'User not authorized' }));
      } else {
        setErrors(prev => ({ ...prev, general: 'Something went wrong' }));
      }
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFiles = (fileList) => {
    const newFiles = Array.from(fileList).map(file => ({
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      id: Math.random().toString(36).substr(2, 9)
    }));
    setFiles(newFiles.slice(0, 1));

  }

  const removeFile = (id) => {
    setFiles(files.filter(file => file.id !== id));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.type) newErrors.type = 'Document type is required';
    if (!formData.category) newErrors.category = 'Document category is required';
    if (!formData.discipline) newErrors.discipline = 'Document discipline is required';
    if (!formData.documentReference) newErrors.documentReference = 'Document reference is required';
    if (!formData.documentDescription) newErrors.documentDescription = 'Description is required';
    if (files.length === 0) newErrors.file = 'Please select a file to upload';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isFormValid = () => {
    return (
      formData.type &&
      formData.category &&
      formData.discipline &&
      formData.documentReference &&
      formData.documentDescription &&
      files.length > 0
    );
  };

  const handleUpload = async () => {
    if (!validateForm()) return;

    const payload = {
      projectName: formData.projectName,
      type: formData.type,
      category: formData.category,
      discipline: formData.discipline,
      // documentReference: formData.documentReference
    };

    try {
      const check = await axios.post("/check-schedule-before-upload", payload);

      // if (check.data.msg !== "Matched") {
      //   // Show Warning Modal
      //   setPendingUploadPayload(true); // just a trigger flag
      //   setShowWarningModal(true);
      //   return;
      // }

      // If matched → upload directly
      proceedWithUpload();

    } catch (err) {
      if (err.response && err.response.data && err.response.data.msg) {
        setErrors(prev => ({ ...prev, general: err.response.data.msg }));
      } else if (err.response && err.response.data && err.response.data.userName) {
        setErrors(prev => ({ ...prev, general: err.response.data.userName[0] }));
      } else if (err.response && err.response.status === 401) {
        setErrors(prev => ({ ...prev, general: 'User not authorized' }));
      } else {
        setErrors(prev => ({ ...prev, general: 'Something went wrong' }));
      }
    }
  };

  // const proceedWithUpload = async () => {
  //   setShowWarningModal(false);
  //   setLoading(true);

  //   const formDataPayload = new FormData();
  //   formDataPayload.append("file", files[0].file);

  //   const data = { ...formData, user, role: role?.trim() };
  //   if (isReupload && originalDocumentCode) {
  //     data.code = originalDocumentCode;
  //   }

  //   formDataPayload.append("data", JSON.stringify(data));

  //   try {
  //     if (isReupload) {
  //       console.log("🚀 API CALLED: /reupload-document");
  //       await axios.post("/reupload-document", formDataPayload, {
  //         headers: { "Content-Type": "multipart/form-data" },
  //         timeout: 600000,
  //       });
  //     } else {
  //       console.log("🚀 API CALLED: /upload-document");
  //       await axios.post("/upload-document", formDataPayload, {
  //         headers: { "Content-Type": "multipart/form-data" },
  //         timeout: 600000,
  //       });
  //     }

  //     setUploadSuccess(true);
  //     message.success("Document uploaded successfully!");

  //     setTimeout(() => {
  //       setUploadSuccess(false);
  //       setFiles([]);
  //       navigate("/home/view-document");
  //     }, 800);

  //   } catch (err) {
  //     if (err.response?.data?.msg) {
  //       setErrors((prev) => ({ ...prev, general: err.response.data.msg }));
  //     } else {
  //       setErrors((prev) => ({ ...prev, general: "Upload failed" }));
  //     }
  //   }

  //   setLoading(false);
  // };

  const proceedWithUpload = async () => {
    setShowWarningModal(false);
    setLoading(true);

    const formDataPayload = new FormData();
    formDataPayload.append("file", files[0].file);

    /* =========================
      BUILD COMMERCIAL PAYLOAD
      ========================= */
    let commercialValues = null;
    let commercialPaymentReceipt = null;

    if (isIPA || isIPC) {
      commercialValues = {
        reference: isIPA
          ? categoryFields.ipaReference
          : categoryFields.ipcReference,

        preliminaries: categoryFields.preliminaries,
        workDone: categoryFields.workDone,
        provisionalSums: categoryFields.provisionalSums,
        approvedVariation: categoryFields.approvedVariations,
        materialsOnSite: categoryFields.materialsOnSite,
        claimsAmounts: categoryFields.claimsOther,
        advancesAdjustments: categoryFields.advancesAdjustments,
        deductionsPenalty: categoryFields.deductionsPenalty,
        total: categoryFields.total,
        lessPrevCertified: categoryFields.lessPrevCertified,
        netAmountDue: categoryFields.netAmountDue,
        vat: categoryFields.vat,
        grandTotal: categoryFields.grandTotal
      };
    }

    if (isPYR) {
      commercialPaymentReceipt = {
        chequeNo: categoryFields.chequeNo,
        paymentReceived: categoryFields.paymentReceived,
        ipcReference: categoryFields.ipcRef,
        remarks: categoryFields.remarks
      };
    }

    /* =========================
      FINAL DATA PAYLOAD
      ========================= */
    const data = {
      ...formData,
      user,
      role: role?.trim(),
      commercialValues,
      commercialPaymentReceipt
    };

    if (isReupload && originalDocumentCode) {
      data.code = originalDocumentCode;
    }

    formDataPayload.append("data", JSON.stringify(data));

    try {
      if (isReupload) {
        await axios.post("/reupload-document", formDataPayload, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 600000,
        });
      } else {
        await axios.post("/upload-document", formDataPayload, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 600000,
        });
      }

      setUploadSuccess(true);
      message.success("Document uploaded successfully!");

      setTimeout(() => {
        setUploadSuccess(false);
        setFiles([]);
        navigate("/home/view-document");
      }, 800);

    } catch (err) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.response?.data?.msg ||
        "Upload failed";

      message.error(errorMessage);

      setErrors((prev) => ({
        ...prev,
        general: errorMessage,
      }));
    }

    setLoading(false);
  };

  const uploadResponseDocument = async () => {
    if (!files.length) {
      message.error("Please select a file");
      return;
    }

    if (!formData.respondingTo) {
      message.error("Responding To is required");
      return;
    }

    setLoading(true);

    const formDataPayload = new FormData();
    formDataPayload.append("file", files[0].file);

    const data = {
      projectName: formData.projectName,
      type: formData.type,
      category: formData.category,
      discipline: formData.discipline,
      documentReference: formData.documentReference,
      documentDescription: formData.documentDescription,
      respondingTo: formData.respondingTo,
      user,
      role: role?.trim(),
    };

    formDataPayload.append("data", JSON.stringify(data));

    try {
      await axios.post("/response-document", formDataPayload, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 600000,
      });

      message.success("Response document uploaded successfully");
      setUploadSuccess(true);

      setTimeout(() => {
        setUploadSuccess(false);
        setFiles([]);
        navigate("/home/view-document");
      }, 800);

    } catch (err) {
      message.error(
        err?.response?.data?.message ||
        "Failed to upload response document"
      );
    }

    setLoading(false);
  };

  const handleBulkUpload = () => {
    navigate('/home/bulk-upload-document');
  };

  const menu = (
    <Menu>
      <Menu.Item key="bulk-upload" onClick={handleBulkUpload}>
        <span className="flex items-center">
          <FileText className="w-4 h-4 mr-2" />
          Bulk Upload
        </span>
      </Menu.Item>
    </Menu>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 p-4 md:p-8">
      <div className="max-w-4xl mx-auto relative">
        <div className="absolute top-4 right-4 z-50">
          <Dropdown overlay={menu} trigger={['click']}>
            <div className="cursor-pointer p-1 hover:bg-gray-100 rounded-full">
              <MoreVertical className="w-5 h-5 text-gray-600" />
            </div>
          </Dropdown>
        </div>

        <div className="flex justify-center">
          {/* Main Form */}
          <div className="w-full max-w-4xl space-y-6 relative">
            {/* Document Information */}            
            <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
              <div className="p-8 space-y-6">
                {/* Document Type & Category Row */}
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      Document Type *
                    </label>
                    <div className="relative">
                      <select
                        value={formData.type}
                        onChange={(e) => handleInputChange('type', e.target.value)}
                        className={`w-full px-4 py-3 pl-12 bg-white border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                          errors.type ? 'border-red-300' : 'border-gray-200'
                        }`}
                        disabled={isReupload}
                      >
                        <option value="">Select Document Type</option>
                        {documentTypes.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                      <Tag className="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    </div>
                    {errors.type && <p className="text-red-500 text-sm mt-1">{errors.type}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      Document Category *
                    </label>
                    <div className="relative">
                      <select
                        value={formData.category}
                        onChange={(e) => handleInputChange('category', e.target.value)}
                        className={`w-full px-4 py-3 pl-12 bg-white border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                          errors.category ? 'border-red-300' : 'border-gray-200'
                        }`}
                        disabled={isReupload}
                      >
                        <option value="">Select Category</option>
                        {documentCategories.map(category => (
                          <option key={category} value={category}>{category}</option>
                        ))}
                      </select>
                      <FolderOpen className="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    </div>
                    {errors.category && <p className="text-red-500 text-sm mt-1">{errors.category}</p>}
                  </div>
                </div>

                {/* Responding To – ONLY for Upload Response */}
                {isUploadResponse && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      Responding To *
                    </label>
                    <input
                      type="text"
                      value={formData.respondingTo}
                      placeholder="Responding To"
                      className="w-full px-4 py-3 bg-gray-100 border border-gray-200 rounded-xl"
                      disabled
                    />
                  </div>
                )}

                {/* Discipline */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Document Discipline *
                  </label>
                  <div className="relative">
                    <select
                      value={formData.discipline}
                      onChange={(e) => handleInputChange('discipline', e.target.value)}
                      className={`w-full px-4 py-3 pl-12 bg-white border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                        errors.discipline ? 'border-red-300' : 'border-gray-200'
                      }`}
                      disabled={isReupload}
                    >
                      <option value="">Select Discipline</option>
                      {disciplines.map(discipline => (
                        <option key={discipline} value={discipline}>{discipline}</option>
                      ))}
                    </select>
                    <Settings className="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  </div>
                  {errors.discipline && <p className="text-red-500 text-sm mt-1">{errors.discipline}</p>}
                </div>

                {/* Document Reference */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Document Reference *
                  </label>
                  <input
                    type="text"
                    value={formData.documentReference}
                    onChange={(e) => handleInputChange('documentReference', e.target.value)}
                    placeholder="Enter document reference number"
                    className={`w-full px-4 py-3 bg-white border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      errors.documentReference ? 'border-red-300' : 'border-gray-200'
                    }`}
                    disabled={isReupload}
                  />
                  {errors.documentReference && <p className="text-red-500 text-sm mt-1">{errors.documentReference}</p>}
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Description *
                  </label>
                  <textarea
                    value={formData.documentDescription}
                    onChange={(e) => handleInputChange('documentDescription', e.target.value)}
                    placeholder="Provide a detailed description of the document..."
                    rows={4}
                    className={`w-full px-4 py-3 bg-white border rounded-xl resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                      errors.documentDescription ? 'border-red-300' : 'border-gray-200'
                    }`}
                    disabled={isReupload}
                  />
                  {errors.documentDescription && <p className="text-red-500 text-sm mt-1">{errors.documentDescription}</p>}
                </div>

                {isIPA && (
                  <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 space-y-6">
                    <h3 className="text-lg font-bold text-gray-800">IPA Details</h3>

                    {/* IPA Reference */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        IPA Reference
                      </label>
                      <input
                        type="text"
                        value={categoryFields.ipaReference}
                        onChange={(e) =>
                          handleCategoryFieldChange("ipaReference", e.target.value)
                        }
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl"
                      />
                    </div>

                    <div className="grid grid-cols-1 gap-6">
                      <AmountInput
                        label="Preliminaries"
                        value={categoryFields.preliminaries}
                        onChange={(v) =>
                          handleCategoryFieldChange("preliminaries", v)
                        }
                      />

                      <AmountInput
                        label="Work Done"
                        value={categoryFields.workDone}
                        onChange={(v) =>
                          handleCategoryFieldChange("workDone", v)
                        }
                      />

                      <AmountInput
                        label="Provisional Sums"
                        value={categoryFields.provisionalSums}
                        onChange={(v) =>
                          handleCategoryFieldChange("provisionalSums", v)
                        }
                      />

                      <AmountInput
                        label="Approved Variations"
                        value={categoryFields.approvedVariations}
                        onChange={(v) =>
                          handleCategoryFieldChange("approvedVariations", v)
                        }
                      />

                      <AmountInput
                        label="Materials on Site"
                        value={categoryFields.materialsOnSite}
                        onChange={(v) =>
                          handleCategoryFieldChange("materialsOnSite", v)
                        }
                      />

                      <AmountInput
                        label="Claims & Other Amounts"
                        value={categoryFields.claimsOther}
                        onChange={(v) =>
                          handleCategoryFieldChange("claimsOther", v)
                        }
                      />

                      <AmountInput
                        label="Advances & Adjustments"
                        value={categoryFields.advancesAdjustments}
                        onChange={(v) =>
                          handleCategoryFieldChange("advancesAdjustments", v)
                        }
                      />

                      <AmountInput
                        label="Deductions & Penalty"
                        value={categoryFields.deductionsPenalty}
                        onChange={(v) =>
                          handleCategoryFieldChange("deductionsPenalty", v)
                        }
                      />

                      <AmountInput
                        label="Total"
                        value={categoryFields.total}
                        readOnly
                      />

                      <AmountInput
                        label="Less: Previously Certified"
                        value={categoryFields.lessPrevCertified}
                        onChange={(v) =>
                          handleCategoryFieldChange("lessPrevCertified", v)
                        }
                      />

                      <AmountInput
                        label="Net Amount Due"
                        value={categoryFields.netAmountDue}
                        readOnly
                      />

                      <AmountInput
                        label="VAT"
                        value={categoryFields.vat}
                        onChange={(v) =>
                          handleCategoryFieldChange("vat", v)
                        }
                      />

                      <AmountInput
                        label="Grand Total"
                        value={categoryFields.grandTotal}
                        readOnly
                      />
                    </div>

                  </div>
                )}

                {isIPC && (
                  <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 space-y-6">
                    <h3 className="text-lg font-bold text-gray-800">IPC Details</h3>

                    {/* IPC Reference */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        IPC Reference
                      </label>
                      <input
                        type="text"
                        value={categoryFields.ipcReference}
                        onChange={(e) =>
                          handleCategoryFieldChange("ipcReference", e.target.value)
                        }
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl"
                      />
                    </div>

                    <div className="grid grid-cols-1 gap-6">
                      <AmountInput
                        label="Preliminaries"
                        value={categoryFields.preliminaries}
                        onChange={(v) => handleCategoryFieldChange("preliminaries", v)}
                      />

                      <AmountInput
                        label="Work Done"
                        value={categoryFields.workDone}
                        onChange={(v) => handleCategoryFieldChange("workDone", v)}
                      />

                      <AmountInput
                        label="Provisional Sums"
                        value={categoryFields.provisionalSums}
                        onChange={(v) => handleCategoryFieldChange("provisionalSums", v)}
                      />

                      <AmountInput
                        label="Approved Variations"
                        value={categoryFields.approvedVariations}
                        onChange={(v) => handleCategoryFieldChange("approvedVariations", v)}
                      />

                      <AmountInput
                        label="Materials on Site"
                        value={categoryFields.materialsOnSite}
                        onChange={(v) => handleCategoryFieldChange("materialsOnSite", v)}
                      />

                      <AmountInput
                        label="Claims & Other Amounts"
                        value={categoryFields.claimsOther}
                        onChange={(v) => handleCategoryFieldChange("claimsOther", v)}
                      />

                      <AmountInput
                        label="Advances & Adjustments"
                        value={categoryFields.advancesAdjustments}
                        onChange={(v) => handleCategoryFieldChange("advancesAdjustments", v)}
                      />

                      <AmountInput
                        label="Deductions & Penalty"
                        value={categoryFields.deductionsPenalty}
                        onChange={(v) => handleCategoryFieldChange("deductionsPenalty", v)}
                      />

                      <AmountInput
                        label="Total"
                        value={categoryFields.total}
                        readOnly
                      />

                      <AmountInput
                        label="Less: Previously Certified"
                        value={categoryFields.lessPrevCertified}
                        onChange={(v) => handleCategoryFieldChange("lessPrevCertified", v)}
                      />

                      <AmountInput
                        label="Net Amount Due"
                        value={categoryFields.netAmountDue}
                        readOnly
                      />

                      <AmountInput
                        label="VAT"
                        value={categoryFields.vat}
                        onChange={(v) => handleCategoryFieldChange("vat", v)}
                      />

                      <AmountInput
                        label="Grand Total"
                        value={categoryFields.grandTotal}
                        readOnly
                      />
                    </div>
                  </div>
                )}

                {isPYR && (
                  <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 space-y-6">
                    <h3 className="text-lg font-bold text-gray-800">
                      Payment Receipt Details
                    </h3>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Cheque No
                        </label>
                        <input
                          type="text"
                          value={categoryFields.chequeNo}
                          onChange={(e) => handleCategoryFieldChange("chequeNo", e.target.value)}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl"
                        />
                      </div>

<AmountInput
  label="Payment Received"
  value={categoryFields.paymentReceived}
  onChange={(v) =>
    handleCategoryFieldChange("paymentReceived", v)
  }
/>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          IPC Reference
                        </label>
                        <input
                          type="text"
                          value={categoryFields.ipcRef}
                          onChange={(e) => handleCategoryFieldChange("ipcRef", e.target.value)}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Remarks
                      </label>
                      <textarea
                        rows={3}
                        value={categoryFields.remarks}
                        onChange={(e) => handleCategoryFieldChange("remarks", e.target.value)}
                        className="w-full px-4 py-3 border border-gray-200 rounded-xl resize-none"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* File Upload Section */}
            <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
              <div className="p-8 space-y-6">
                {/* Drag & Drop Zone */}
                <div
                  className={`relative border-2 border-dashed rounded-2xl p-6 text-center transition-all duration-300 ${
                    dragActive 
                      ? 'border-green-500 bg-green-50' 
                      : errors.file
                      ? 'border-red-300 bg-red-50'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  <input
                    type="file"
                    onChange={(e) => handleFiles(e.target.files)}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  />
                  
                  <div className="space-y-2">
                    <div className="flex justify-center">
                      <div className={`w-20 h-20 rounded-full flex items-center justify-center transition-colors ${
                        dragActive ? 'bg-green-100' : errors.file ? 'bg-red-100' : 'bg-gray-100'
                      }`}>
                        <File className={`w-10 h-10 ${
                          dragActive ? 'text-green-600' : errors.file ? 'text-red-500' : 'text-gray-400'
                        }`} />
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">
                        {dragActive ? 'Drop file here' : 'Upload your document'}
                      </h3>
                      <p className="text-gray-500 mb-4">
                        Drag and drop a file here, or click to browse
                      </p>
                      <p className="text-sm text-gray-400">
                        Supports PDF, DOC, DOCX, JPG, PNG file
                      </p>
                    </div>
                  </div>
                </div>

                {errors.file && (
                  <div className="flex items-center text-red-600">
                    <AlertCircle className="w-4 h-4 mr-2" />
                    <span className="text-sm">{errors.file}</span>
                  </div>
                )}

                {/* Selected File */}
                {files.length > 0 && (
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-4">Selected File</h4>
                    {files.map((file) => (
                      <div key={file.id} className="flex items-center justify-between p-4 bg-green-50 border border-green-200 rounded-xl">
                        <div className="flex items-center space-x-3">
                          <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                            <File className="w-6 h-6 text-green-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{file.name}</p>
                            <p className="text-sm text-gray-500">{formatFileSize(file.size)}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => removeFile(file.id)}
                          className="w-8 h-8 bg-red-100 hover:bg-red-200 rounded-full flex items-center justify-center transition-colors"
                        >
                          <X className="w-4 h-4 text-red-600" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Upload Button */}
                <div className="flex justify-center">
                  <Button
                    type="primary"
                    onClick={isUploadResponse ? uploadResponseDocument : handleUpload}
                    loading={loading}
                    disabled={!isFormValid() || loading}
                    size="large"
                    style={{
                      width: '256px',
                      height: '56px',
                      fontSize: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '16px',
                    }}
                  >
                    {loading ? (
                      isUploadResponse
                        ? 'Uploading Response...'
                        : isReupload
                        ? 'Reuploading...'
                        : 'Uploading...'
                    ) : (
                      <span className="flex items-center">
                        <UploadIcon className="w-5 h-5 mr-2 inline-block" />
                        {isUploadResponse
                          ? 'Upload Response'
                          : isReupload
                          ? 'Reupload Document'
                          : 'Upload Document'}
                      </span>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6 max-w-md mx-auto">
            {/* Progress Indicator */}
            {loading && (
              <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4">
                <div className="flex items-center space-x-3 mb-3">
                  <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse"></div>
                  <span className="text-blue-700 font-medium">Processing your document...</span>
                </div>
                <div className="bg-blue-200 rounded-full h-2">
                  <div className="bg-blue-600 h-2 rounded-full animate-pulse" style={{width: '70%'}}></div>
                </div>
              </div>
            )}

            {/* Success Message */}
            {uploadSuccess && (
              <div className="bg-green-50 border border-green-200 rounded-2xl p-4">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="w-6 h-6 text-green-600" />
                  <div>
                    <p className="text-green-800 font-semibold">Upload Successful!</p>
                    <p className="text-green-600 text-sm">Your document has been uploaded successfully.</p>
                  </div>
                </div>
              </div>
            )}

            {/* General Error Message */}
            {errors.general && (
              <div className="bg-red-50 border border-red-200 rounded-2xl p-4">
                <div className="flex items-center space-x-3">
                  <AlertCircle className="w-6 h-6 text-red-600" />
                  <div>
                    <p className="text-red-800 font-semibold">Error</p>
                    <p className="text-red-600 text-sm">{errors.general}</p>
                  </div>
                </div>
              </div>
            )}

            {showWarningModal && (
              <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
                <div className="bg-white p-6 rounded-xl shadow-xl max-w-sm w-full">
                  <h2 className="text-xl font-semibold text-red-600 mb-3">Warning</h2>
                  <p className="text-gray-700 mb-6">
                    The selected fields are not matching with the uploaded Schedule.<br />
                    Do you want to continue uploading?
                  </p>

                  <div className="flex justify-between">
                    <button
                      onClick={() => {
                        setShowWarningModal(false);
                        setPendingUploadPayload(null);
                      }}
                      className="px-4 py-2 bg-gray-300 rounded-lg"
                    >
                      Cancel
                    </button>

                    <button
                      onClick={proceedWithUpload}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg"
                    >
                      Confirm Upload
                    </button>
                  </div>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentUploadForm;